<script setup lang="ts">
import type { MailItem, MailListItem } from '@/api/mail/type'
import FooterBar from '@/components/footer-bar/FooterBar.vue'
import { useCommonStore } from '@/stores/modules/common'
import { Select } from '@element-plus/icons-vue'
import { dayjs } from 'element-plus'
import { nextTick, ref, watch, computed } from 'vue'
import MailDetail from './components/MailDetail.vue'
import { socketEmitAsync } from '@/utils/web-socket'
import ReceiveItem from './components/ReceiveItem.vue'
import { useUserStore } from '@/stores/modules/user'
import type { ReceiveMailRes } from '@/types/mail'
import type { ItemType } from '@/types/item'
import { autoSellQuality, commonMailTitleList, mailCategoryList, getMailCategory } from './data'
import type { MailCategory } from './data'
import type { ConsumablesModeType } from '@/types/consumables'
import type { PetType } from '@/types/pet'
import { QuestionFilled } from '@element-plus/icons-vue'
import type { RuneItemType } from '@/types/rune'
import type { DailyChallengeModeType } from '@/types/user'
import type { RelicItemType } from '@/types/relic'
import type { SkillItemType } from '@/types/skill'
import { getDailyChallengeTimesGrowth } from '@/utils/daily-challenge'

const envMode = import.meta.env.VITE_ENV_MODE

const commonStore = useCommonStore()
const userStore = useUserStore()

const curTab = ref('unread')

const mailId = ref<number>()
const mailDetailShow = ref(false)
function openMailDetail(item: MailListItem) {
  mailDetailShow.value = true
  mailId.value = item.id
}

const receiveTitle = ref('')
const receiveItemShow = ref(false)
const receiveItem = ref<ItemType[]>([])
const receiveGold = ref(0)
const receiveDiamond = ref(0)
const receiveCrystal = ref(0)
const receiveDuelCrystal = ref(0)
const receiveConsumables = ref<Partial<Record<ConsumablesModeType, number>>>({})
const receivePetList = ref<PetType[]>([])
const receiveRuneList = ref<RuneItemType[]>([])
const receiveRelicList = ref<RelicItemType[]>([])
const receiveSkillList = ref<SkillItemType[]>([])
type MailReceiveShowData = Pick<MailItem, 'title'> & Partial<MailItem>

function handleShowReceive(item: MailReceiveShowData, isReceive: boolean) {
  receiveTitle.value = item.title
  receiveItem.value = item.itemList || []
  receiveGold.value = item.gold || 0
  receiveDiamond.value = item.diamond || 0
  receiveCrystal.value = item.crystal || 0
  receiveDuelCrystal.value = item.duelCrystal || 0
  receiveConsumables.value = item.consumables || {}
  receiveItemShow.value = true
  receivePetList.value = item.petList || []
  receiveRuneList.value = item.runeList || []
  receiveRelicList.value = item.relicList || []
  receiveSkillList.value = item.skillList || []
  if (isReceive) {
    mailDetailShow.value = false
  }
  commonStore.getMailList()
}
async function handleBatchReceive() {
  if (userStore.getItemEquipTotal >= userStore.getItemCapacity) return ElMessage.warning('背包已满')
  if (userStore.gold > 10000000000) return ElMessage.warning('金币已达上限，无法领取邮件')

  const unReceiveMailList = commonStore.mailList.filter((item) => !item.isGet)
  if (!unReceiveMailList.length) return ElMessage.warning('暂无邮件可领取')
  const loading = ElLoading.service({
    lock: true,
    text: '正在领取邮件...',
    background: 'rgba(0, 0, 0, 0.7)',
  })
  receiveItem.value = []
  receiveGold.value = 0
  receiveDiamond.value = 0
  receiveCrystal.value = 0
  receiveDuelCrystal.value = 0
  receiveConsumables.value = {}
  receivePetList.value = []
  receiveRuneList.value = []
  receiveRelicList.value = []
  receiveSkillList.value = []
  try {
    const quality = []
    if (autoSell.value) {
      const index = autoSellQuality.findIndex((item) => item.key === autoSell.value)
      for (let i = 1; i <= index; i++) {
        quality.push(autoSellQuality[i].key)
      }
    }
    if (userStore.getItemEquipTotal >= userStore.getItemCapacity) return ElMessage.warning('装备背包已满')
    if (userStore.runeList.length >= userStore.getRuneCapacity) return ElMessage.warning('符石背包已满')
    if (userStore.skillList.length >= userStore.getSkillCapacity) return ElMessage.warning('技能背包已满')
    if (userStore.petList.length >= userStore.getPetCapacity) return ElMessage.warning('魔物牧场已满')
    const res = await socketEmitAsync<ReceiveMailRes>({ type: 'receiveMail', data: { idList: unReceiveMailList.map((item) => item.id), quality } })
    const { itemList, gold, diamond, crystal, duelCrystal, vipLevel, consumables, petList, runeList, relicList, skillList } = res
    if (itemList) {
      userStore.itemList.push(...itemList)
      receiveItem.value.push(...itemList)
    }
    if (consumables) {
      let key: ConsumablesModeType
      for (key in consumables) {
        userStore.consumables[key] += consumables[key]!
      }
      receiveConsumables.value = consumables
    }
    if (petList) {
      userStore.petList.push(...petList)
      receivePetList.value.push(...petList)
    }
    if (runeList) {
      userStore.runeList.push(...runeList)
      receiveRuneList.value.push(...runeList)
    }
    if (relicList?.length) {
      userStore.getRelicToBag(relicList)
      receiveRelicList.value.push(...relicList)
    }
    if (skillList?.length) {
      userStore.skillList.push(...res.skillList)
      receiveSkillList.value.push(...res.skillList)
    }
    userStore.gold += gold
    receiveGold.value += gold
    userStore.diamond += diamond
    receiveDiamond.value += diamond
    userStore.crystal += crystal
    userStore.duelCrystal += duelCrystal
    receiveCrystal.value += crystal
    receiveDuelCrystal.value += duelCrystal
    if (vipLevel > userStore.vipLevel) {
      const growTimes = getDailyChallengeTimesGrowth(userStore.vipLevel, vipLevel)
      let key: DailyChallengeModeType
      for (key in userStore.dailyChallengeTimes) {
        userStore.dailyChallengeTimes[key] += growTimes
      }
      // 魔物巢穴次数增加
      if (userStore.vipLevel === 0 && vipLevel === 3) {
        userStore.nestTimes += 2
      } else if (!(userStore.vipLevel === 1 && vipLevel === 2)) {
        userStore.nestTimes++
      }
      // 符石遗迹次数增加
      if (userStore.soulType && userStore.vipLevel === 0) {
        userStore.runeRuinTimes++
      }

      userStore.vipLevel = vipLevel
    }
    if (unReceiveMailList.length) {
      receiveItemShow.value = true
    }
  } catch (err) {
    console.error(err)
  } finally {
    nextTick(() => {
      commonStore.getMailList()
      loading.close()
    })
  }
}

// 邮件分类
const unreadCollapse = ref<MailCategory[]>(['dungeon', 'special'])
const readCollapse = ref<MailCategory[]>(['dungeon', 'special'])

function groupByCategory(list: MailListItem[], isGet = false) {
  const groups: Record<MailCategory, MailListItem[]> = { common: [], dungeon: [], special: [] }
  for (const item of list) {
    groups[getMailCategory(item.title)].push(item)
  }
  if (groups.special.length) {
    if (!isGet) {
      unreadCollapse.value = ['special']
    } else {
      readCollapse.value = ['special']
    }
  }
  return groups
}

const unreadGrouped = computed(() =>
  groupByCategory(
    commonStore.mailList.filter((item) => !item.isGet),
    false,
  ),
)
const readGrouped = computed(() =>
  groupByCategory(
    commonStore.mailList.filter((item) => item.isGet),
    true,
  ),
)

const autoSell = ref('')
function handleChangeAutoSellQuality(quality: string) {
  localStorage.setItem(`war-soul-${envMode === 'test' ? 'test-' : ''}auto-sell`, quality)
  autoSell.value = quality
}
const autoSellName = computed(() => autoSellQuality.find((item) => item.key === autoSell.value)?.name || '')

const getAutoSellSliceArr = computed(() => {
  const isAdvance = userStore.advanceLevel === 4
  const isPeak = userStore.soulType > 3
  if (isPeak) {
    return [6]
  } else if (isAdvance) {
    return [3, 7]
  } else {
    return [1, 7]
  }
})

const displayAutoSellQuality = computed(() => {
  const [start, end] = getAutoSellSliceArr.value

  return [autoSellQuality[0], ...autoSellQuality.slice(start, end)]
})

watch(
  () => commonStore.isSocketInit,
  (val) => {
    if (!val) return

    commonStore.getMailList()
    autoSell.value = localStorage.getItem(`war-soul-${envMode === 'test' ? 'test-' : ''}auto-sell`) || ''
  },
)
</script>

<template>
  <div class="mail-page page">
    <h2>邮件</h2>
    <div class="action affix">
      <el-button type="primary" size="small" plain @click="handleBatchReceive">一键领取</el-button>
      <el-popover placement="bottom" :width="150" trigger="click" popper-class="monster-detail" :teleported="false">
        <template #reference>
          <el-button size="small" plain>
            自动出售装备：<span :class="autoSell">{{ autoSellName || '关闭' }}</span>
          </el-button>
        </template>
        <div class="btn-column-list">
          <el-button v-for="item of displayAutoSellQuality" :key="item.key" size="small" plain @click="handleChangeAutoSellQuality(item.key)">
            <span :class="item.key">{{ item.name }}</span>
            <el-popover placement="top" trigger="click">
              <template #reference>
                <el-icon style="margin-left: 5px" size="12" v-show="item.key === 'special'"><QuestionFilled /></el-icon>
              </template>
              <span>自动出售秘钻级别及以下的史诗装备</span>
            </el-popover>
          </el-button>
        </div>
      </el-popover>
    </div>
    <b style="font-size: 12px; color: red">* 邮件最多保存一个月</b>

    <el-tabs v-model="curTab" stretch>
      <el-tab-pane label="未领取" name="unread" class="pane">
        <div class="mail-list">
          <el-collapse v-model="unreadCollapse">
            <template v-for="cat of mailCategoryList" :key="cat.key">
              <el-collapse-item v-if="unreadGrouped[cat.key].length" :name="cat.key">
                <template #title>
                  <span class="category-title">
                    <span class="category-icon">{{ cat.icon }}</span>
                    <span>{{ cat.label }}</span>
                    <el-badge :value="unreadGrouped[cat.key].length" :max="50" class="category-badge" />
                  </span>
                </template>
                <div class="item border-wrap" v-for="item of unreadGrouped[cat.key]" :key="item.id" @click="openMailDetail(item)">
                  <div class="info">
                    <p class="title">
                      <span :class="cat.key">{{ item.title }}</span>
                    </p>
                    <p class="from">By: {{ item.from }}</p>
                    <p class="date">{{ dayjs(item.created).format('YYYY-MM-DD HH:mm:ss') }}</p>
                  </div>
                  <div class="is-get">
                    <span>
                      <el-icon v-show="item.isGet"><Select></Select></el-icon>
                    </span>
                  </div>
                </div>
              </el-collapse-item>
            </template>
          </el-collapse>
        </div>
      </el-tab-pane>
      <el-tab-pane label="已领取" name="readed" class="pane">
        <div class="mail-list">
          <el-collapse v-model="readCollapse">
            <template v-for="cat of mailCategoryList" :key="cat.key">
              <el-collapse-item v-if="readGrouped[cat.key].length" :name="cat.key">
                <template #title>
                  <span class="category-title">
                    <span class="category-icon">{{ cat.icon }}</span>
                    <span>{{ cat.label }}</span>
                    <el-badge :value="readGrouped[cat.key].length" :max="50" class="category-badge" />
                  </span>
                </template>
                <div class="item border-wrap" v-for="item of readGrouped[cat.key]" :key="item.id" @click="openMailDetail(item)">
                  <div class="info">
                    <p class="title affix">
                      <span :class="cat.key">{{ item.title }}</span>
                    </p>
                    <p class="from">By: {{ item.from }}</p>
                    <p class="date">{{ dayjs(item.created).format('YYYY-MM-DD HH:mm:ss') }}</p>
                  </div>
                  <div class="is-get">
                    <span>
                      <el-icon v-show="item.isGet"><Select></Select></el-icon>
                    </span>
                  </div>
                </div>
              </el-collapse-item>
            </template>
          </el-collapse>
        </div>
      </el-tab-pane>
    </el-tabs>

    <el-dialog v-model="mailDetailShow" width="100%" style="max-width: 500px" destroy-on-close>
      <template v-if="mailId">
        <MailDetail :id="mailId" @showReceive="handleShowReceive" />
      </template>
      <template #footer>
        <p style="text-align: center">
          <el-button type="info" plain @click="mailDetailShow = false" size="small">关闭</el-button>
        </p>
      </template>
    </el-dialog>

    <el-dialog v-model="receiveItemShow" width="100%" style="max-width: 500px">
      <ReceiveItem
        :receiveTitle="receiveTitle"
        :receiveItem="receiveItem"
        :receiveGold="receiveGold"
        :receiveDiamond="receiveDiamond"
        :receiveCrystal="receiveCrystal"
        :receiveDuelCrystal="receiveDuelCrystal"
        :receiveConsumables="receiveConsumables"
        :receivePetList="receivePetList"
        :receiveRuneList="receiveRuneList"
        :receiveRelicList="receiveRelicList"
        :receive-skill-list="receiveSkillList"
        @close="receiveItemShow = false"
      />
      <template #footer>
        <p style="text-align: center">
          <el-button plain @click="receiveItemShow = false" size="small">关闭</el-button>
        </p>
      </template>
    </el-dialog>

    <FooterBar />
  </div>
</template>

<style lang="scss" scoped>
.mail-page {
  padding-top: 0;
  display: flex;
  flex-flow: column nowrap;
  overflow: hidden;
  .action {
    margin-bottom: 10px;
  }
  .mail-list {
    height: 100%;
    overflow: auto;
    .item {
      margin-bottom: 10px;
      padding: 5px 10px;
      display: flex;
      align-items: center;
      justify-content: space-between;
      &:last-child {
        margin-bottom: 0;
      }
      .info {
        p {
          margin: 4px 0;
        }
        .title {
          font-weight: bold;
          .dungeon {
            color: #68be8d;
          }
          .special {
            color: #f7c99c;
          }
        }
        .date {
          opacity: 0.8;
          font-size: 12px;
        }
      }
      .is-get {
        span {
          display: inline-block;
          border: 2px var(--fontColor) solid;
          border-radius: 2px;
          padding: 2px;
          height: 25px;
          width: 25px;
          display: flex;
          align-items: center;
          justify-content: center;
        }
      }
    }
  }
  .el-tabs {
    flex: 1;
    overflow: hidden;
    display: flex;
    flex-flow: column nowrap;
    :deep() {
      .el-tabs__content {
        flex: 1;
        .pane {
          height: 100%;
          .pane-item {
            height: 100%;
            overflow: hidden;
            display: flex;
            flex-flow: column nowrap;
          }
        }
      }
    }
  }
  .el-popover {
    max-height: 60vh;
    overflow: auto;
  }
  .el-collapse {
    --el-collapse-border-color: transparent;
    --el-collapse-header-bg-color: transparent;
    --el-collapse-content-bg-color: transparent;
    border: none;
    .el-collapse-item {
      margin-bottom: 8px;
      :deep(.el-collapse-item__header) {
        height: 36px;
        line-height: 36px;
        border-bottom: 1px solid rgba(255, 255, 255, 0.08);
        font-size: 14px;
      }
      :deep(.el-collapse-item__content) {
        padding-bottom: 0;
      }
    }
  }
  .category-title {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    .category-icon {
      font-size: 16px;
    }
    .category-badge {
      margin-left: 4px;
      :deep(.el-badge__content) {
        font-size: 10px;
      }
    }
  }
}
</style>
